/**
 * Cmarkdowntable Component
 * Table component for markdown rendering
 */

'use client';

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import {
  IconCopy,
  IconDownload,
  IconFileTypeCsv,
  IconMarkdown,
  IconPhoto,
} from '@tabler/icons-react';
import { useClipboard } from '@/hooks/dom';
import { IMAGE_COPY_TYPE, useCopyElementAsImage } from '@/hooks/files';
import { useMessageModal } from '@/contexts/layout';
import { Temper } from '@/types/frontend-types';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton } from '../Cbutton';
import { Cdropdown } from '../Cdropdown';
import { Cmarkdownvisual } from '../Cmarkdownvisual';
import { CmarkdowntableBody } from './CmarkdowntableBody';
import { CmarkdowntableCompactPreview } from './CmarkdowntableCompactPreview';
import {
  ICON_BTN_CONTAINER_STYLE,
  ICON_BTN_ICON_SIZE,
  ICON_BTN_RADIUS,
  ICON_BTN_SIZE,
  TABLE_DEFAULT_EXPANDED_MAX_ROWS,
  TABLE_DROPDOWN_ALIGN_RIGHT,
  TABLE_EXPORT_FILENAME,
  TABLE_EXPORT_MIME_TYPE,
  TABLE_EXPORT_OPTION_KEY,
  TABLE_IMAGE_COPY_ERROR_BODY,
  TABLE_IMAGE_COPY_ERROR_BUTTON_TEXT,
  TABLE_IMAGE_COPY_ERROR_TITLE,
  TABLE_METRIC_UNIT,
  TABLE_VISUAL_TYPE,
} from './Cmarkdowntable.constants';
import {
  downloadBlob,
  tableToCsv,
  tableToMarkdown,
} from './Cmarkdowntable.helpers';
import type { CmarkdowntableProps } from './Cmarkdowntable.types';

const OPEN_VALUE = "open";
const CLOSED_VALUE = "closed";
const MENU_OPEN_VALUE = "menuOpen";
const MENU_CLOSED_VALUE = "menuClosed";

export type {
  CmarkdowntableCell,
  CmarkdowntableProps,
  CmarkdowntableTextAlign,
} from './Cmarkdowntable.types';

export function Cmarkdowntable(props: CmarkdowntableProps): ReactElement {
  const { dataElScope, ...bodyProps } = props;
  const {
    headerCells,
    rows,
    className,
    style,
    textAlign,
    defaultExpanded,
  } = bodyProps;

  const [canvasMenuOpen, setCanvasMenuOpen] = useState(false);
  const [modalMenuOpen, setModalMenuOpen] = useState(false);
  const tableElementRef = useRef<HTMLDivElement | null>(null);
  const clipboard = useClipboard();
  const copyElementAsImage = useCopyElementAsImage();
  const { showMessageModal } = useMessageModal();

  const csvText = useMemo(
    () => tableToCsv({ headerCells, rows }),
    [headerCells, rows],
  );
  const markdownText = useMemo(
    () => tableToMarkdown({ headerCells, rows, textAlign }),
    [headerCells, rows, textAlign],
  );

  const handleDownloadCsv = useCallback((): void => {
    downloadBlob({
      blob: new Blob([csvText], { type: TABLE_EXPORT_MIME_TYPE.Csv }),
      filename: TABLE_EXPORT_FILENAME.Csv,
    });
  }, [csvText]);

  const handleCopyMarkdown = useCallback((): void => {
    clipboard.invoke({ text: markdownText });
  }, [clipboard, markdownText]);

  const handleCopyCsv = useCallback((): void => {
    clipboard.invoke({ text: csvText });
  }, [clipboard, csvText]);

  const handleCopyImage = useCallback((): void => {
    const tableElement = tableElementRef.current;
    if (!tableElement) return;
    copyElementAsImage.invoke(
      {
        element: tableElement,
        type: IMAGE_COPY_TYPE.Png,
        options: {
          pixelRatio: typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1,
          cacheBust: true,
          backgroundColor: 'rgba(8,22,16,1)',
        },
      },
      {
        onError: () => {
          showMessageModal({
            temper: Temper.Neutral,
            title: TABLE_IMAGE_COPY_ERROR_TITLE,
            body: TABLE_IMAGE_COPY_ERROR_BODY,
            secondaryButtonText: TABLE_IMAGE_COPY_ERROR_BUTTON_TEXT,
          });
        },
      },
    );
  }, [copyElementAsImage, showMessageModal]);

  const copyMenuOptions = useMemo(() => [
    { key: TABLE_EXPORT_OPTION_KEY.Markdown, label: 'Copy as Markdown', icon: IconMarkdown, onClick: handleCopyMarkdown },
    { key: TABLE_EXPORT_OPTION_KEY.Csv, label: 'Copy as CSV', icon: IconFileTypeCsv, onClick: handleCopyCsv },
    { key: TABLE_EXPORT_OPTION_KEY.Image, label: 'Copy as Image', icon: IconPhoto, onClick: handleCopyImage },
  ], [handleCopyMarkdown, handleCopyCsv, handleCopyImage]);

  const renderControls = (isOpen: boolean, setOpen: (open: boolean) => void): ReactElement => (
    <>
      <Cdropdown
        dataElScope={dataElScope}
        trigger={(
          <Cbutton
            icon={IconCopy}
            iconSize={ICON_BTN_ICON_SIZE}
            preset={ButtonPreset.Ghost}
            radius={ICON_BTN_RADIUS}
            padding={0}
            height={ICON_BTN_SIZE}
            containerStyle={ICON_BTN_CONTAINER_STYLE}
          />
        )}
        options={copyMenuOptions}
        isOpen={isOpen}
        onOpenChange={setOpen}
        align={TABLE_DROPDOWN_ALIGN_RIGHT}
        width={220}
      />
      <Cbutton
        icon={IconDownload}
        iconSize={ICON_BTN_ICON_SIZE}
        preset={ButtonPreset.Ghost}
        radius={ICON_BTN_RADIUS}
        padding={0}
        height={ICON_BTN_SIZE}
        containerStyle={ICON_BTN_CONTAINER_STYLE}
        onClick={handleDownloadCsv}
      />
    </>
  );

  const compactPreview = useMemo(() => {
    return <CmarkdowntableCompactPreview dataElScope={dataElScope} rows={rows} />;
  }, [dataElScope, rows]);

  return (
    <Cmarkdownvisual
      dataElScope={dataElScope}
      type={TABLE_VISUAL_TYPE}
      className={className}
      style={style}
      compactPreview={compactPreview}
      defaultExpanded={
        defaultExpanded ?? rows.length < TABLE_DEFAULT_EXPANDED_MAX_ROWS
      }
      metric={{ value: rows.length, unit: TABLE_METRIC_UNIT }}
      controls={renderControls(canvasMenuOpen, setCanvasMenuOpen)}
      modalControls={renderControls(modalMenuOpen, setModalMenuOpen)}
    >
      <CmarkdowntableBody dataElScope={dataElScope} {...bodyProps} tableElementRef={tableElementRef} />
    </Cmarkdownvisual>
  );
}
